<script lang="ts">
  import ContentBlockBody from "@shared/svelte/Components/ContentBlock/ContentBlockBody.svelte";
  import CustomContentCollaterals from "@shared/svelte/Components/CustomContentCollaterals/CustomContentCollaterals.svelte";
  import ContentBlockHighlights from "@shared/svelte/Components/ContentBlock/ContentBlockHighlights.svelte";
  import ContentBlockImage from "@shared/svelte/Components/ContentBlock/ContentBlockImage.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { CustomContentContentBlockEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { reformatUrlAndGetTargetValue } from "@shared/services/target_value_forward_urls";

  import type {
    CustomContentCollateral,
    CustomContentContentBlockHighlight,
  } from "@shared/types/CustomContent";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";

  const { trackClick, trackMrcEvents } = EventTracking;

  export let id: number;
  export let position: number;
  export let title: string;
  export let description: string;
  export let forward_url: string;
  export let image_alt_text: string;
  export let image_upload_url: string;
  export let image_left_aligned: boolean;
  export let include_spacer: boolean = false;
  export let mobileMode: boolean;

  // Unused props passed from discovery panel
  export let type: string = null;
  type;
  export let item_type: string = null;
  item_type;
  export let image_upload_id: number = null;
  image_upload_id;

  export let collaterals_attributes: CustomContentCollateral[] = [];
  export let highlights_attributes: CustomContentContentBlockHighlight[] = [];

  const highlights = highlights_attributes.map(h => h.highlight_text);

  let displayImageBeforeText: boolean = false;
  let displayIsVertical: boolean = window.innerWidth < 700;
  const setImageLayout = () => {
    // 700 is where fizz causes everything to go vertical, so we have to put the image first at this point.
    displayIsVertical = window.innerWidth < 700;
    displayImageBeforeText = image_left_aligned || displayIsVertical;
  };
  setImageLayout();
  const trackingParams: CustomContentContentBlockEntity = new CustomContentContentBlockEntity({
    id,
    position,
  });

  const { href, target } = reformatUrlAndGetTargetValue(forward_url);

  let hoveringOnImage: boolean = false;
  const handleClick = (entities: BaseEntityClass[]) => {
    trackClick("custom_content_content_block", entities);
  };
</script>

<svelte:window on:resize={setImageLayout} />
<TrackingContext entities={trackingParams} let:trackingContext>
  <div
    class="fizz-align-stretch fizz-stack-16"
    class:fizz-inset-squish-32={!mobileMode}
    class:fizz-inset-squish-16={mobileMode}
    use:trackMrcEvents={[trackingContext.entities]}
    on:resize={setImageLayout}
    data-testid="content-block"
  >
    <div class="fizz-stack-16" class:fizz-border-top={include_spacer} />
    <div class="fizz-flex-row-responsive">
      {#if displayImageBeforeText}
        <ContentBlockImage
          {image_upload_url}
          {image_alt_text}
          {href}
          {target}
          dataTestIdSuffix={"before-body"}
          {handleClick}
          bind:mobileMode
          bind:hoveringOnImage
          bind:displayIsVertical
        />
      {/if}

      <ContentBlockBody
        {title}
        {description}
        {href}
        {target}
        {handleClick}
        {highlights}
        bind:mobileMode
        bind:hoveringOnImage
      />
      {#if !displayImageBeforeText}
        <ContentBlockImage
          {image_upload_url}
          {image_alt_text}
          {href}
          {target}
          dataTestIdSuffix={"after-body"}
          {handleClick}
          bind:mobileMode
          bind:hoveringOnImage
          bind:displayIsVertical
        />
      {/if}
    </div>

    {#if mobileMode}
      <ContentBlockHighlights {highlights} dataTestIdSuffix="after-body" />
    {/if}
    <CustomContentCollaterals
      dataTestIdBase={"content-block-collaterals"}
      collaterals={collaterals_attributes}
      alignCollateralRight={image_left_aligned}
      bind:mobileMode
      bind:displayIsVertical
    />
  </div>
</TrackingContext>
